import React from 'react';
import {
    Autocomplete,
    TextField,
    FormControl,
    CircularProgress,
    Tooltip,
    IconButton
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Control, Controller } from 'react-hook-form';

interface Option {
    label: string;
    value: string;
}

interface ComboboxWithFreeTextProps {
    control: Control<any>;
    name: string;
    label: string;
    options?: Option[];
    isDisabled?: boolean;
    defaultValue?: string;
    isLoading?: boolean;
    state?: ComboboxState;
    stateText?: string;
}

export enum ComboboxState {
    NO_ACTION = 'NO_ACTION',
    WARNING = 'WARNING',
}
export const ComboboxWithFreeText: React.FC<ComboboxWithFreeTextProps> = ({
    control,
    name,
    label,
    options = [],
    isDisabled = false,
    defaultValue = '',
    isLoading = false,
    state = ComboboxState.NO_ACTION,
    stateText = ''
}) => {
    return (
        <FormControl fullWidth>
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Autocomplete
                        freeSolo
                        disabled={isDisabled}
                        options={options}
                        value={value || ''}
                        onChange={(_, newValue) => {
                            if (typeof newValue === 'string') {
                                onChange(newValue);
                            } else if (newValue && 'value' in newValue) {
                                onChange(newValue.value);
                            } else {
                                onChange('');
                            }
                        }}
                        onInputChange={(_, newInputValue) => {
                            onChange(newInputValue || '');
                        }}
                        getOptionLabel={(option) => {
                            if (!option) return '';
                            if (typeof option === 'string') {
                                return option;
                            }
                            return option?.label ?? '';
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                error={!!error}
                                helperText={error ? "Geçerli değer giriniz." : ''}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        ...(state === ComboboxState.WARNING && {
                                            '& fieldset': {
                                                borderColor: 'warning.main',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'warning.dark',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'warning.main',
                                            },
                                        }),
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoading ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : null}
                                            {state === ComboboxState.WARNING && (
                                                <Tooltip
                                                    title={stateText}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        size="medium"
                                                        sx={{
                                                            color: 'warning.main',
                                                            padding: '4px',
                                                            '&:hover': {
                                                                backgroundColor: 'warning.light',
                                                            },
                                                        }}
                                                    >
                                                        <WarningAmberIcon fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        loading={isLoading}
                        loadingText="Yükleniyor..."
                    />
                )}
            />
        </FormControl>
    );
};